import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    VStack,
    HStack,
    Text,
    Box,
    Button,
    Input,
    Select,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from './ManageDialogFrame';
import {
    useModelViewportsStore,
    VPORTS_CELL_COUNT,
    type NamedVportsConfig,
} from '../../state/useModelViewportsStore';

/** Human face for a stored config: preset + join summary. */
function configLabel(cfg: NamedVportsConfig): string {
    const joined = cfg.spans.filter((g) => g.length > 1).length;
    return joined > 0
        ? `${cfg.config.toUpperCase()} (${joined} joined)`
        : cfg.config.toUpperCase();
}

/**
 * VPORTS dialog (Faz 10.5 — named viewport configurations): Save the current
 * tiling under a name, Restore/Delete saved ones, and Join two adjacent
 * cells of the current tiling (AutoCAD Join; OCS has no Join verb).
 */
export function VportsDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'vports');
    const onClose = useAppStore((state) => state.closeManageDialog);

    const config = useModelViewportsStore((s) => s.config);
    const spans = useModelViewportsStore((s) => s.spans);
    const namedConfigs = useModelViewportsStore((s) => s.namedConfigs);
    const joinCells = useModelViewportsStore((s) => s.joinCells);
    const saveNamed = useModelViewportsStore((s) => s.saveNamed);
    const restoreNamed = useModelViewportsStore((s) => s.restoreNamed);
    const deleteNamed = useModelViewportsStore((s) => s.deleteNamed);

    const [name, setName] = useState('');
    const [selected, setSelected] = useState<string | null>(null);
    const [joinA, setJoinA] = useState('1');
    const [joinB, setJoinB] = useState('2');
    const [message, setMessage] = useState<string | null>(null);

    if (!isOpen) return null;

    const names = Object.keys(namedConfigs).sort();
    const cellCount = VPORTS_CELL_COUNT[config];
    const joinedCount = spans.filter((g) => g.length > 1).length;

    const runJoin = () => {
        const err = joinCells(Number(joinA) - 1, Number(joinB) - 1);
        setMessage(err ?? t('vports.joinApplied'));
    };

    return (
        <ManageDialogFrame
            title={t('vports.title')}
            isOpen={isOpen}
            onClose={onClose}
            footer={<ManageDialogCloseFooter onClose={onClose} />}
        >
            <VStack spacing={4} align="stretch">
                <Text fontSize="xs" color="var(--color-text-secondary)">
                    {t('vports.current', {
                        config: config.toUpperCase(),
                        cells: cellCount,
                        joined: joinedCount,
                    })}
                </Text>

                {/* Save current tiling under a name. */}
                <HStack spacing={2}>
                    <Input
                        size="sm"
                        value={name}
                        placeholder={t('vports.namePlaceholder')}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <Button
                        size="sm"
                        colorScheme="blue"
                        isDisabled={!name.trim()}
                        flexShrink={0}
                        onClick={() => {
                            saveNamed(name);
                            setSelected(name.trim());
                            setName('');
                            setMessage(t('vports.saved'));
                        }}
                    >
                        {t('vports.save')}
                    </Button>
                </HStack>

                {/* Saved configurations: pick one, restore or delete it. */}
                <Box borderWidth="1px" borderRadius="var(--cad-radius-sm)" borderColor="var(--color-border)">
                    <VStack spacing={0} align="stretch" maxH="30vh" overflowY="auto">
                        {names.length === 0 ? (
                            <Text p={3} fontSize="xs" color="var(--color-text-secondary)">
                                {t('vports.noneSaved')}
                            </Text>
                        ) : names.map((n) => (
                            <Box
                                key={n}
                                px={3}
                                py={2}
                                cursor="pointer"
                                bg={selected === n ? 'var(--color-bg-tertiary)' : 'transparent'}
                                onClick={() => setSelected(n)}
                            >
                                <HStack justify="space-between">
                                    <Text fontSize="sm" isTruncated>{n}</Text>
                                    <Text fontSize="xs" color="var(--color-text-secondary)" flexShrink={0}>
                                        {configLabel(namedConfigs[n])}
                                    </Text>
                                </HStack>
                            </Box>
                        ))}
                    </VStack>
                </Box>
                <HStack spacing={2}>
                    <Button
                        size="sm"
                        flexShrink={0}
                        isDisabled={!selected}
                        onClick={() => {
                            if (selected && restoreNamed(selected)) {
                                setMessage(t('vports.restored', { name: selected }));
                            }
                        }}
                    >
                        {t('vports.restore')}
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        flexShrink={0}
                        isDisabled={!selected}
                        onClick={() => {
                            if (selected && deleteNamed(selected)) {
                                setMessage(t('vports.deleted', { name: selected }));
                                setSelected(null);
                            }
                        }}
                    >
                        {t('vports.delete')}
                    </Button>
                </HStack>

                {/* Join two adjacent cells of the current tiling. */}
                <Text fontSize="xs" color="var(--color-text-secondary)" pt={1}>
                    {t('vports.joinHint', { cells: cellCount })}
                </Text>
                <HStack spacing={2}>
                    <Select size="sm" value={joinA} onChange={(e) => setJoinA(e.target.value)} w="auto">
                        {Array.from({ length: cellCount }, (_, i) => (
                            <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                        ))}
                    </Select>
                    <Text fontSize="sm" flexShrink={0}>+</Text>
                    <Select size="sm" value={joinB} onChange={(e) => setJoinB(e.target.value)} w="auto">
                        {Array.from({ length: cellCount }, (_, i) => (
                            <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                        ))}
                    </Select>
                    <Button size="sm" flexShrink={0} onClick={runJoin}>
                        {t('vports.join')}
                    </Button>
                </HStack>

                {message ? (
                    <Text fontSize="xs" color="var(--color-text-secondary)">{message}</Text>
                ) : null}
            </VStack>
        </ManageDialogFrame>
    );
}
